import { getCsrfToken } from "@/lib/csrf";
import type {
	IncentiveRule,
	PackageOptionGroup,
} from "@/types/admin-portal/bonus-ro";

export interface PackageRules {
	packageId: number;
	packageName: string;
	numberOfVisit: number | null;
	/** oldest first */
	rules: IncentiveRule[];
	/**
	 * The package's last rule, the one with no end day. Only it may be re-priced: a new
	 * open-ended rule after an earlier one would overlap the successor already scheduled.
	 */
	openRule: IncentiveRule | null;
}

export interface ServiceRules {
	serviceName: string;
	packages: PackageRules[];
}

const byName = (a: string, b: string) => a.localeCompare(b, "id");

export function groupRules(rules: IncentiveRule[]): ServiceRules[] {
	const services = new Map<string, Map<number, PackageRules>>();
	const chronological = [...rules].sort((a, b) =>
		a.validFrom.localeCompare(b.validFrom),
	);

	for (const rule of chronological) {
		const packages =
			services.get(rule.serviceName) ?? new Map<number, PackageRules>();
		services.set(rule.serviceName, packages);

		const group = packages.get(rule.packageId) ?? {
			packageId: rule.packageId,
			packageName: rule.packageName,
			numberOfVisit: rule.numberOfVisit,
			rules: [],
			openRule: null,
		};
		packages.set(rule.packageId, group);

		group.rules.push(rule);
		if (rule.validUntil === null) group.openRule = rule;
	}

	return [...services]
		.sort(([a], [b]) => byName(a, b))
		.map(([serviceName, packages]) => ({
			serviceName,
			packages: [...packages.values()].sort((a, b) =>
				byName(a.packageName, b.packageName),
			),
		}));
}

export interface AddablePackageGroup {
	serviceName: string;
	packages: { id: number; name: string; minStartsOn: string }[];
}

export function newRuleStartsAfterClosedRules(
	packageRules: IncentiveRule[],
	today: string,
): string {
	return packageRules.reduce(
		(day, r) => (r.validUntil ? laterDay(day, dayAfter(r.validUntil)) : day),
		today,
	);
}

export function addablePackages(
	options: PackageOptionGroup[],
	rules: IncentiveRule[],
	today: string,
): AddablePackageGroup[] {
	const open = new Set(
		rules.filter((r) => r.validUntil === null).map((r) => r.packageId),
	);
	return options
		.map((group) => ({
			serviceName: group.serviceName,
			packages: group.packages
				.filter((p) => !open.has(p.id))
				.map((p) => ({
					...p,
					minStartsOn: newRuleStartsAfterClosedRules(
						rules.filter((r) => r.packageId === p.id),
						today,
					),
				})),
		}))
		.filter((group) => group.packages.length > 0);
}

/** A whole Rupiah amount; "85.000" reads as 85000. null when it is not one. */
export function parseAmount(text: string): number | null {
	const digits = text.replace(/[.\s]/g, "");
	return /^\d+$/.test(digits) ? Number(digits) : null;
}

/** The YYYY-MM-DD day after `day`, computed in UTC so no browser time zone shifts it. */
export function dayAfter(day: string): string {
	const date = new Date(`${day}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + 1);
	return date.toISOString().slice(0, 10);
}

export const laterDay = (a: string, b: string) => (a > b ? a : b);

/** A YYYY-MM-DD WIB day for display, read in UTC so no browser time zone shifts it. */
export function formatDay(day: string): string {
	return new Date(`${day}T00:00:00Z`).toLocaleDateString("id-ID", {
		timeZone: "UTC",
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

export type WriteResult =
	| { ok: true }
	| {
			ok: false;
			error: string;
			/** set when a re-price closed the old rule but could not create the new one */
			ruleClosedUntil?: string;
	  };

/** Sends a Bonus RO write to the Rails proxy; the backend's message is kept for display. */
export async function sendWrite(
	url: string,
	method: "POST" | "PUT",
	body: Record<string, unknown>,
	fallbackError: string,
): Promise<WriteResult> {
	try {
		const res = await fetch(url, {
			method,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": getCsrfToken(),
			},
			body: JSON.stringify(body),
		});
		const json = await res.json().catch(() => null);
		if (res.ok && json?.success) return { ok: true };
		return {
			ok: false,
			error: json?.error || fallbackError,
			ruleClosedUntil: json?.ruleClosedUntil,
		};
	} catch {
		return { ok: false, error: fallbackError };
	}
}
